// @ts-nocheck
/**
 * Prevented matchups — "these two teams must not play each other".
 *
 * One row per pair, matched in either direction, held as an approved operator
 * do_not_play constraint. Adding a pair never deletes a game: if something on
 * the board already pairs them, this panel says so and leaves the decision
 * with the director.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Trash2, AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';

const call = async (body: Record<string, unknown>) => {
  const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', { body });
  if (error) {
    let message = error.message || 'Request failed';
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message = parsed?.error?.message || message;
      }
    } catch { /* keep the original message */ }
    return { ok: false, message };
  }
  if (!res?.ok) return { ok: false, message: res?.error?.message ?? 'Request failed' };
  return { ok: true, data: res.data };
};

interface Props {
  eventId: string;
  division: string;
  /** Called after a pair is added or removed so pool plans re-read them. */
  onChanged?: () => void;
}

const PreventedMatchups: React.FC<Props> = ({ eventId, division, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState<any[]>([]);
  const [pairs, setPairs] = useState<any[]>([]);
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [notes, setNotes] = useState('');
  /** Games already on the board that the pair just added breaks. */
  const [breaking, setBreaking] = useState<any[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await call({ action: 'prevented_pairs_list', event_id: eventId, division });
    setLoading(false);
    if (!res.ok) { toast.error(res.message); return; }
    setTeams(res.data?.teams ?? []);
    setPairs(res.data?.pairs ?? []);
  }, [eventId, division]);

  useEffect(() => { void load(); }, [load]);

  const nameOf = (teamId: string) =>
    teams.find((t) => t.team_id === teamId)?.name ?? 'Team';

  const optionsB = useMemo(
    () => teams.filter((t) => t.team_id !== teamA),
    [teams, teamA],
  );

  const add = async () => {
    if (!teamA || !teamB) return;
    setBusy(true);
    const res = await call({
      action: 'prevented_pairs_add',
      event_id: eventId,
      division,
      team_id: teamA,
      opponent_team_id: teamB,
      notes: notes || undefined,
    });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return; }
    const hit = res.data?.existing_games_breaking ?? [];
    setBreaking(hit.length ? hit : null);
    if (hit.length) {
      toast.warning(`${nameOf(teamA)} and ${nameOf(teamB)} already have a game on the board.`);
    } else {
      toast.success('Prevented matchup added.');
    }
    setTeamA(''); setTeamB(''); setNotes('');
    await load();
    onChanged?.();
  };

  const remove = async (constraintId: string) => {
    setBusy(true);
    const res = await call({
      action: 'prevented_pairs_remove', event_id: eventId, constraint_id: constraintId,
    });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return; }
    setBreaking(null);
    toast.success('Prevented matchup removed.');
    await load();
    onChanged?.();
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Ban className="h-3.5 w-3.5 text-muted-foreground" />
        <Label className="text-xs">Prevented matchups</Label>
        {(loading || busy) && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Two teams that must never meet in {division}. New pool and cross-pool games skip the pair,
        and the schedule cannot be published while a game still pairs them.
      </p>

      {!loading && teams.length < 2 && (
        <p className="text-[11px] text-muted-foreground">
          {division} needs at least two registered teams before a matchup can be prevented.
        </p>
      )}

      {teams.length >= 2 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Team</Label>
            <Select value={teamA} onValueChange={setTeamA}>
              <SelectTrigger className="h-8 w-52 text-sm"><SelectValue placeholder="Choose a team" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Must not play</Label>
            <Select value={teamB} onValueChange={setTeamB}>
              <SelectTrigger className="h-8 w-52 text-sm"><SelectValue placeholder="Choose a team" /></SelectTrigger>
              <SelectContent>
                {optionsB.map((t) => (
                  <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            className="h-8 w-56 text-sm"
            placeholder="Why (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Button size="sm" className="h-8 text-xs" disabled={!teamA || !teamB || busy} onClick={() => void add()}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>
      )}

      {breaking && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">
              {breaking.length} game{breaking.length === 1 ? '' : 's'} on the board already pair
              {breaking.length === 1 ? 's' : ''} these teams.
            </p>
            <ul className="mt-1 space-y-0.5">
              {breaking.map((g: any) => (
                <li key={g.game_id}>
                  {g.teams} — {g.placed ? 'on the grid' : 'not placed yet'}
                </li>
              ))}
            </ul>
            <p className="mt-1">
              Nothing has been deleted. Regenerate this division to rebuild without the matchup, or
              delete the game by hand.
            </p>
          </div>
        </div>
      )}

      {pairs.length > 0 && (
        <ul className="space-y-1">
          {pairs.map((p) => (
            <li key={p.constraint_id} className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs">
              <span>
                <span className="font-medium">{p.team_a_name}</span>
                <span className="text-muted-foreground"> must not play </span>
                <span className="font-medium">{p.team_b_name}</span>
                {p.notes && <span className="text-muted-foreground"> — {p.notes}</span>}
              </span>
              <Button
                size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                title="Remove this prevented matchup"
                disabled={busy}
                onClick={() => void remove(p.constraint_id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PreventedMatchups;
